import type { Coding, Extension, Invoice } from "@medplum/fhirtypes";

import {
  STATEMENT_STATUS,
  type StatementStatus,
} from "@/billing/components/StatementsTab/constants/statements";
import { VOID_INVOICE_STATUSES } from "@/constants/billing";
import { Extensions } from "@/fhir/enums/extensions";
import {
  BILLING_STATEMENT_LIFECYCLE_TAGS,
  BillingStatementTag,
  QuiltedHealthCodeSystems,
} from "@/fhir/enums/quilted-health";

import { isCancelledSubStatement } from "./balance-returned";

// Reads a statement's status off an Invoice and writes one back. It is pure, with no React or Medplum-client
// dependency, so the statement-generation bot can import it; the client uses it today through
// re-exports in StatementsTab/utils/statements.ts.

export function getInvoiceClosureDate(invoice: Invoice): string | undefined {
  return invoice.extension?.find((extension) => extension.url === Extensions.StatementClosureDate)
    ?.valueDateTime;
}

// Closing a statement records when it was closed, so these statuses carry a closure date; any
// other status must not, which is why moving away from one clears the extension. The same pair is
// what settles a statement's status for good — see `isStatementStatusLocked`, which routes here so
// the terminal statuses are named once.
export function isClosedStatus(status: StatementStatus): boolean {
  return status === STATEMENT_STATUS.Paid || status === STATEMENT_STATUS.Denied;
}

/**
 * Every tag except the given codes from the billing statement tags CodeSystem. Tags from other
 * systems, and codes in this system outside `codesToDrop`, are preserved — the tag groups in this
 * CodeSystem are independent, so each writer clears only its own group.
 */
export function tagsWithout(
  invoice: Invoice,
  codesToDrop: readonly BillingStatementTag[],
): Coding[] {
  return (
    invoice.meta?.tag?.filter(
      (tag) =>
        !(
          tag.system === QuiltedHealthCodeSystems.BillingStatementTags &&
          codesToDrop.includes(tag.code as BillingStatementTag)
        ),
    ) ?? []
  );
}

/**
 * The statement's current lifecycle tag, if any. Only lifecycle codes are considered — the archive
 * and delete tags share the CodeSystem but sit outside the lifecycle, so matching on the system
 * alone would read one of them back as a status.
 */
function getInvoiceLifecycleTag(invoice: Invoice): BillingStatementTag | undefined {
  return invoice.meta?.tag?.find(
    (tag) =>
      tag.system === QuiltedHealthCodeSystems.BillingStatementTags &&
      BILLING_STATEMENT_LIFECYCLE_TAGS.includes(tag.code as BillingStatementTag),
  )?.code as BillingStatementTag | undefined;
}

/**
 * Whether the statement carries the given code in the billing statement tags CodeSystem.
 *
 * Both halves have to match. Codes in this system are not unique across systems — `deleted` and
 * `archived` also exist under other Quilted CodeSystems — so matching the code alone would read a
 * tag from an unrelated system as a billing marker.
 */
export function hasStatementTag(invoice: Invoice, code: BillingStatementTag): boolean {
  return (
    invoice.meta?.tag?.some(
      (tag) => tag.system === QuiltedHealthCodeSystems.BillingStatementTags && tag.code === code,
    ) ?? false
  );
}

/**
 * Whether a statement is void: cancelled, entered in error, or tagged Deleted. A void statement is
 * not billable and owns no ledger transfer, so family links step over it.
 */
export function isVoidStatement(invoice: Invoice): boolean {
  if (invoice.status && VOID_INVOICE_STATUSES.has(invoice.status)) return true;
  return hasStatementTag(invoice, BillingStatementTag.Deleted);
}

/**
 * A cancelled sub-statement the ledger shows as a return row: `status: cancelled` with a complete
 * balance-returned record. A Deleted-tagged one is void and has no row.
 */
export function isReturnedStatement(invoice: Invoice): boolean {
  return isCancelledSubStatement(invoice) && !hasStatementTag(invoice, BillingStatementTag.Deleted);
}

// Invoice.status stays on its canonical R4 binding (draft | issued | balanced | cancelled |
// entered-in-error); the fine-grained work-queue state lives in meta.tag under a local
// CodeSystem. The `accepted`/`rejected` tags are only ever set on insurance statements (derived
// from a ClaimResponse); self-pay statements only ever see `denied` (manual) or no tag at all.
export function getStatementStatus(invoice: Invoice): StatementStatus {
  const tag = getInvoiceLifecycleTag(invoice);

  // The delete tag is the source of truth for deletion; `Invoice.status: "cancelled"` is written
  // alongside it, so the badge reads the tag and does not depend on the two staying in step.
  if (hasStatementTag(invoice, BillingStatementTag.Deleted)) return STATEMENT_STATUS.Cancelled;

  // A cancelled sub-statement carries `status: cancelled` and the balance-returned record, and is
  // archived rather than deleted, so it reads as Cancelled without the delete tag.
  if (isCancelledSubStatement(invoice)) {
    return STATEMENT_STATUS.Cancelled;
  }

  if (invoice.status === "balanced") return STATEMENT_STATUS.Paid;

  if (invoice.status === "issued") {
    if (tag === BillingStatementTag.Rejected) return STATEMENT_STATUS.Rejected;
    if (tag === BillingStatementTag.Denied) return STATEMENT_STATUS.Denied;
    if (tag === BillingStatementTag.InProcessing) return STATEMENT_STATUS.Processing;
    return STATEMENT_STATUS.Sent;
  }

  return STATEMENT_STATUS.Unsent;
}

interface InvoiceStatusUpdate {
  status: Invoice["status"];
  tag: BillingStatementTag | undefined;
}

// The inverse of getStatementStatus: until ClaimResponse-driven derivation lands, a manual status
// click writes Invoice.status/meta.tag directly for both payer types.
function getInvoiceStatusUpdate(status: StatementStatus): InvoiceStatusUpdate {
  switch (status) {
    case STATEMENT_STATUS.Unsent:
      return { status: "draft", tag: undefined };
    case STATEMENT_STATUS.Sent:
      return { status: "issued", tag: undefined };
    case STATEMENT_STATUS.Processing:
      return { status: "issued", tag: BillingStatementTag.InProcessing };
    case STATEMENT_STATUS.Paid:
      return { status: "balanced", tag: undefined };
    case STATEMENT_STATUS.Rejected:
      return { status: "issued", tag: BillingStatementTag.Rejected };
    case STATEMENT_STATUS.Denied:
      return { status: "issued", tag: BillingStatementTag.Denied };
    case STATEMENT_STATUS.Accepted:
      return { status: "issued", tag: BillingStatementTag.InProcessing };
    case STATEMENT_STATUS.Cancelled:
      return { status: "cancelled", tag: undefined };
  }
}

/**
 * Returns a shallow copy of the invoice with `status` and the lifecycle meta.tag set to match the
 * given badge status, preserving every other tag unchanged — including the archive and delete tags,
 * which share the CodeSystem but are orthogonal to the lifecycle. The statement-closure-date
 * extension follows the status: a closed status writes `closureDate` (or keeps the existing one
 * when none is given), and any other status drops the extension. Every other extension stays in
 * place.
 */
export function applyStatementStatus(
  invoice: Invoice,
  status: StatementStatus,
  closureDate?: string,
): Invoice {
  const { status: nextStatus, tag: nextTag } = getInvoiceStatusUpdate(status);
  const preservedTags = tagsWithout(invoice, BILLING_STATEMENT_LIFECYCLE_TAGS);

  return {
    ...invoice,
    status: nextStatus,
    meta: {
      ...invoice.meta,
      tag: nextTag
        ? [
            ...preservedTags,
            { system: QuiltedHealthCodeSystems.BillingStatementTags, code: nextTag },
          ]
        : preservedTags,
    },
    extension: applyClosureDateExtension(invoice, status, closureDate),
  };
}

function applyClosureDateExtension(
  invoice: Invoice,
  status: StatementStatus,
  closureDate?: string,
): Extension[] | undefined {
  const existingClosureDate = getInvoiceClosureDate(invoice);
  const nextClosureDate = isClosedStatus(status) ? (closureDate ?? existingClosureDate) : undefined;

  if (nextClosureDate === existingClosureDate) return invoice.extension;

  const preservedExtensions =
    invoice.extension?.filter((extension) => extension.url !== Extensions.StatementClosureDate) ??
    [];

  if (!nextClosureDate) {
    return preservedExtensions.length > 0 ? preservedExtensions : undefined;
  }

  return [
    ...preservedExtensions,
    { url: Extensions.StatementClosureDate, valueDateTime: nextClosureDate },
  ];
}
